import { useEffect, useState, useCallback } from "react";
import {
  Plus, ShareNetwork, Trash, DownloadSimple, Copy, FacebookLogo, InstagramLogo,
  WhatsappLogo, XLogo, Eye, LinkSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatCard } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ImageCaptionField } from "@/components/ImageCaptionField";

const API = process.env.REACT_APP_BACKEND_URL;
const BLANK = { source: "listing", ref_id: "", headline: "", subline: "", price_label: "", image_id: "", caption: "", cta: "" };

export const shareText = (s) => `${s.caption || s.headline}\n\n${(s.hashtags || []).join(" ")}`;

export default function Stories() {
  const { user } = useAuth();
  const [stories, setStories] = useState([]);
  const [listings, setListings] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...BLANK });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [s, l, a, sh] = await Promise.all([
      api.get("/stories"),
      api.get("/listings"),
      api.get("/announcements"),
      api.get("/shipments"),
    ]);
    setStories(s.data);
    setListings(l.data);
    setAnnouncements(a.data);
    setShipments(sh.data);
    s.data.slice(0, 20).forEach((story) => api.post(`/stories/${story.id}/seen`).catch(() => {}));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/stories", {
        ...form,
        ref_id: form.ref_id || null,
        image_id: form.image_id || null,
      });
      toast.success("Story card created — ready to share");
      setOpen(false);
      setForm({ ...BLANK });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const publicUrl = (s) => `${window.location.origin}/s/${s.token}`;

  const share = async (s, channel) => {
    const url = publicUrl(s);
    const text = shareText(s);
    api.post(`/stories/${s.id}/share`, null, { params: { channel } }).catch(() => {});
    if (channel === "facebook") {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`, "_blank");
    } else if (channel === "whatsapp") {
      window.open(`https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`, "_blank");
    } else if (channel === "x") {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, "_blank");
    } else if (channel === "instagram") {
      navigator.clipboard?.writeText(`${text}\n${url}`)?.catch(() => {});
      if (navigator.share) {
        try {
          await navigator.share({ title: s.headline, text, url });
          return;
        } catch {}
      }
      downloadCard(s);
      toast.success("Caption copied and story card downloaded — open Instagram and post it as a story or reel");
    } else {
      navigator.clipboard?.writeText(url)?.catch(() => {});
      toast.success("Story link copied");
    }
  };

  const downloadCard = (s) => {
    if (!s.card_image_id) {
      toast.error("This story has no card image");
      return;
    }
    const a = document.createElement("a");
    a.href = `${API}/api/public/story-card/${s.token}`;
    a.download = `story-${s.token}.jpg`;
    a.target = "_blank";
    a.click();
  };

  const copyCaption = (s) => {
    navigator.clipboard?.writeText(`${shareText(s)}\n${publicUrl(s)}`)?.catch(() => {});
    toast.success("Caption and link copied");
  };

  const remove = async (id) => {
    try {
      await api.delete(`/stories/${id}`);
      toast.success("Story deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const totalShares = stories.reduce(
    (sum, s) => sum + Object.values(s.shares || {}).reduce((a, b) => a + b, 0), 0
  );
  const refOptions = form.source === "listing" ? listings.map((l) => [l.id, l.title])
    : form.source === "announcement" ? announcements.map((a) => [a.id, a.title])
    : form.source === "shipment" ? shipments.map((s) => [s.id, `${s.shipment_number} · ${s.description}`])
    : [];

  return (
    <div data-testid="stories-page">
      <PageHeader
        overline="Social"
        title="Stories & Reels"
        description="Turn adverts, shipments and announcements into ready-made 1080×1920 story cards, then post them to Facebook, Instagram, WhatsApp or X in one tap."
        testid="stories-header"
        action={
          <Button data-testid="new-story-button" onClick={() => { setForm({ ...BLANK }); setOpen(true); }} className="w-full sm:w-auto">
            <Plus size={16} className="mr-1.5" /> Create story
          </Button>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Stories" value={stories.length} icon={ShareNetwork} testid="stat-stories" />
        <StatCard label="Shares" value={totalShares} tone="primary" icon={ShareNetwork} testid="stat-shares" />
        <StatCard label="Views" value={stories.reduce((s, x) => s + (x.views || 0), 0)} icon={Eye} testid="stat-story-views" />
        <StatCard label="Adverts ready" value={listings.filter((l) => l.status === "active").length} icon={Plus} testid="stat-adverts-ready" />
      </div>

      {stories.length === 0 ? (
        <EmptyState
          title="No stories yet"
          description="Create one from an advert, a shipment or an announcement — the card, caption and hashtags are built for you."
          testid="stories-empty"
        />
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="stories-list">
          {stories.map((s) => (
            <div key={s.id} className="group overflow-hidden border border-border bg-white" data-testid={`story-card-${s.id}`}>
              {s.card_image_id ? (
                <img
                  src={`${API}/api/public/story-card/${s.token}`}
                  alt={s.headline}
                  loading="lazy"
                  className="aspect-[9/16] w-full bg-slate-900 object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                  data-testid={`story-image-${s.id}`}
                />
              ) : (
                <div className="flex aspect-[9/16] w-full items-center justify-center bg-slate-900 text-sm text-slate-400">
                  Card image unavailable
                </div>
              )}
              <div className="p-4">
                <div className="overline text-[0.6rem] text-[#002FA7]">{s.source} · {s.author_name}</div>
                <div className="mt-1 font-display text-base font-semibold leading-snug">{s.headline}</div>
                <p className="mt-1 line-clamp-2 text-xs text-slate-500">{s.caption}</p>
                <div className="mt-1 text-[0.65rem] text-slate-400">
                  {s.views || 0} views · {Object.values(s.shares || {}).reduce((a, b) => a + b, 0)} shares
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <button data-testid={`share-facebook-${s.id}`} onClick={() => share(s, "facebook")} title="Share to Facebook"
                    className="flex h-9 w-9 items-center justify-center border border-border text-[#1877F2] transition-colors hover:bg-[#1877F2] hover:text-white">
                    <FacebookLogo size={17} weight="fill" />
                  </button>
                  <button data-testid={`share-instagram-${s.id}`} onClick={() => share(s, "instagram")} title="Post to Instagram"
                    className="flex h-9 w-9 items-center justify-center border border-border text-[#E1306C] transition-colors hover:bg-[#E1306C] hover:text-white">
                    <InstagramLogo size={17} weight="fill" />
                  </button>
                  <button data-testid={`share-whatsapp-${s.id}`} onClick={() => share(s, "whatsapp")} title="Share on WhatsApp"
                    className="flex h-9 w-9 items-center justify-center border border-border text-[#25D366] transition-colors hover:bg-[#25D366] hover:text-white">
                    <WhatsappLogo size={17} weight="fill" />
                  </button>
                  <button data-testid={`share-x-${s.id}`} onClick={() => share(s, "x")} title="Share on X"
                    className="flex h-9 w-9 items-center justify-center border border-border text-[#0F172A] transition-colors hover:bg-[#0F172A] hover:text-white">
                    <XLogo size={17} weight="fill" />
                  </button>
                  <button data-testid={`copy-caption-${s.id}`} onClick={() => copyCaption(s)} title="Copy caption + link"
                    className="flex h-9 w-9 items-center justify-center border border-border text-slate-600 transition-colors hover:bg-slate-100">
                    <Copy size={16} />
                  </button>
                  <button data-testid={`download-card-${s.id}`} onClick={() => downloadCard(s)} title="Download story card"
                    className="flex h-9 w-9 items-center justify-center border border-border text-slate-600 transition-colors hover:bg-slate-100">
                    <DownloadSimple size={16} />
                  </button>
                  <a href={`/s/${s.token}`} target="_blank" rel="noreferrer" title="Open public page"
                    className="flex h-9 w-9 items-center justify-center border border-border text-slate-600 transition-colors hover:bg-slate-100"
                    data-testid={`open-story-${s.id}`}>
                    <LinkSimple size={16} />
                  </a>
                  {(user?.role === "admin" || s.created_by === user?.id) && (
                    <button data-testid={`delete-story-${s.id}`} onClick={() => remove(s.id)} title="Delete story"
                      className="flex h-9 w-9 items-center justify-center border border-border text-[#FF3B30] transition-colors hover:bg-[#FF3B30] hover:text-white">
                      <Trash size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="story-dialog">
          <DialogHeader>
            <DialogTitle>Create a story card</DialogTitle>
            <DialogDescription>
              We build a 1080×1920 card with your photo, price and brand, plus a caption with hashtags ready to paste.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div>
              <Label>Build it from</Label>
              <Select value={form.source} onValueChange={(v) => setForm({ ...BLANK, source: v })}>
                <SelectTrigger data-testid="story-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="listing">Marketplace advert</SelectItem>
                  <SelectItem value="shipment">Shipment / delivery</SelectItem>
                  <SelectItem value="announcement">Announcement</SelectItem>
                  <SelectItem value="custom">Something custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.source !== "custom" && (
              <div>
                <Label>Pick one</Label>
                <Select value={form.ref_id} onValueChange={(v) => setForm({ ...form, ref_id: v })}>
                  <SelectTrigger data-testid="story-ref-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {refOptions.map(([id, label]) => (
                      <SelectItem key={id} value={id}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Headline {form.source !== "custom" && <span className="text-xs text-slate-400">(optional override)</span>}</Label>
              <Input data-testid="story-headline-input" required={form.source === "custom"} value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} />
            </div>
            <div>
              <Label>Sub-line</Label>
              <Textarea data-testid="story-subline-input" rows={2} value={form.subline} onChange={(e) => setForm({ ...form, subline: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Price badge</Label>
                <Input data-testid="story-price-input" value={form.price_label} onChange={(e) => setForm({ ...form, price_label: e.target.value })} placeholder="$180" />
              </div>
              <div>
                <Label>Call to action</Label>
                <Input data-testid="story-cta-input" value={form.cta} onChange={(e) => setForm({ ...form, cta: e.target.value })} placeholder="DM us to buy" />
              </div>
            </div>
            {form.source === "custom" && (
              <ImageCaptionField
                value={form.image_id}
                onChange={({ image_id }) => setForm({ ...form, image_id: image_id || "" })}
                testid="story-image"
              />
            )}
            <div>
              <Label>Caption (optional — we write one if empty)</Label>
              <Textarea data-testid="story-caption-input" rows={2} value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-story-button" disabled={busy} className="w-full sm:w-auto">
                {busy ? "Building card…" : "Create story card"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
